import type { AlgoliaProduct } from '@jane/search/types';
import type { MenuProduct, PriceId } from '@jane/shared/types';

import { maybeParseFloat } from './pricing/maybeParseFloat';
import { isSoldByWeight } from './storeMenu';

export interface MenuRowRules {
  product_threshold?: {
    maximum_price?: number | null;
    minimum_price?: number | null;
  } | null;
}

export interface CustomRowSetting {
  display_name?: string | null;
  enabled?: boolean;
  id?: number;
  menu_row_type?: string | null;
  row_type?: string;
  rules?: MenuRowRules;
}

export const shouldShowWeightForMenuRow = (
  weightId: PriceId,
  product: MenuProduct | AlgoliaProduct,
  menuRowRules: MenuRowRules
): boolean => {
  if (!isSoldByWeight(product.kind)) return true;

  const productThreshold = menuRowRules?.product_threshold;
  if (!productThreshold) return true;

  const price = maybeParseFloat(product[`price_${weightId}`]);
  if (price == null) return false;

  const { minimum_price, maximum_price } = productThreshold;
  if (minimum_price != null && price < minimum_price) return false;
  if (maximum_price != null && price > maximum_price) return false;

  return true;
};

export const createWeightFilterFromRules = (
  rules?: MenuRowRules
):
  | ((weight: string, product: MenuProduct | AlgoliaProduct) => boolean)
  | undefined => {
  if (!rules?.product_threshold) return undefined;

  return (weight: string, product: MenuProduct | AlgoliaProduct): boolean => {
    return shouldShowWeightForMenuRow(weight as PriceId, product, rules);
  };
};

interface GetMenuRowWeightFilterOptions {
  customRowSettings?: readonly CustomRowSetting[];
  rowId?: number;
  rowTitle?: string;
}

export const findCustomRowSetting = ({
  customRowSettings,
  rowId,
  rowTitle,
}: GetMenuRowWeightFilterOptions): CustomRowSetting | null => {
  if (!customRowSettings) return null;

  if (rowId) {
    return customRowSettings.find((row) => row.id === rowId) || null;
  }

  if (rowTitle) {
    const normalizedTitle = rowTitle.toLowerCase().replace(/_/g, '-');
    return (
      customRowSettings.find((row) => {
        const normalizedRowType = row.row_type
          ?.toLowerCase()
          .replace(/_/g, '-');
        const normalizedMenuRowType = row.menu_row_type
          ?.toLowerCase()
          .replace(/_/g, '-');
        const normalizedDisplayName = row.display_name
          ?.toLowerCase()
          .replace(/_/g, '-');
        return (
          normalizedRowType === normalizedTitle ||
          normalizedMenuRowType === normalizedTitle ||
          normalizedDisplayName === normalizedTitle ||
          row.display_name === rowTitle ||
          row.row_type === rowTitle ||
          row.menu_row_type === rowTitle
        );
      }) || null
    );
  }

  return null;
};

type WeightFilter = (
  weight: string,
  product: MenuProduct | AlgoliaProduct
) => boolean;

/**
 * Creates a weight filter function for a menu row based on price threshold rules.
 */
export const getMenuRowWeightFilter = ({
  customRowSettings,
  rowId,
  rowTitle,
}: GetMenuRowWeightFilterOptions): WeightFilter | undefined => {
  const customRowSetting = findCustomRowSetting({
    customRowSettings,
    rowId,
    rowTitle,
  });

  const rules = customRowSetting?.rules;
  if (!rules?.product_threshold) return undefined;

  return (weight: string, product: MenuProduct | AlgoliaProduct): boolean => {
    return shouldShowWeightForMenuRow(weight as PriceId, product, rules);
  };
};

interface StoreWithCustomRowSettings {
  custom_row_settings?: CustomRowSetting[];
}

/**
 * Convenience function to get weight filter from a store's custom row settings.
 */
export const getWeightFilterForFeaturedCategory = (
  store: StoreWithCustomRowSettings,
  featuredCategory?: string
): WeightFilter | undefined => {
  if (!featuredCategory) return undefined;
  return getMenuRowWeightFilter({
    customRowSettings: store.custom_row_settings,
    rowTitle: featuredCategory,
  });
};
